import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import axios from "axios";
import { useLang } from "../i18n";
import { Logo } from "./Header";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Shell({ children }) {
  const navigate = useNavigate();
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="border-b border-white/10 bg-[#04122e]/70 backdrop-blur-xl">
        <div className="mx-auto flex h-[72px] max-w-7xl items-center px-5 md:px-8">
          <Logo onClick={() => navigate("/")} />
        </div>
      </header>
      <main className="flex flex-1 items-center justify-center px-5 py-20">{children}</main>
    </div>
  );
}

export function PaymentSuccess() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("verifying");

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch {
        setState("error");
        return;
      }
      attempts += 1;
      if (attempts < 12) setTimeout(poll, 2000);
      else setState("pending");
    };
    poll();
  }, [sessionId]);

  return (
    <Shell>
      <motion.div
        data-testid="payment-success-page"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-lg text-center"
      >
        {state === "verifying" ? (
          <>
            <Loader2 size={56} className="mx-auto animate-spin text-primary" />
            <h1 className="mt-8 font-serif text-4xl text-white" data-testid="payment-status-text">
              {t.payment.verifying}
            </h1>
          </>
        ) : (
          <>
            <CheckCircle2 size={64} className="mx-auto text-primary" strokeWidth={1.4} />
            <h1 className="mt-8 font-serif text-5xl tracking-tight text-white md:text-6xl">
              {t.payment.successTitle}
            </h1>
            <p className="mt-5 text-base leading-relaxed text-white/65 md:text-lg" data-testid="payment-status-text">
              {state === "pending" ? t.payment.pendingText : t.payment.successText}
            </p>
          </>
        )}
        <button
          data-testid="back-home-button"
          onClick={() => navigate("/")}
          className="mt-10 rounded-full bg-primary px-9 py-4 text-[13px] font-semibold uppercase tracking-[0.14em] text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#e0562a]"
        >
          {t.payment.backHome}
        </button>
      </motion.div>
    </Shell>
  );
}

export function PaymentCancel() {
  const { t } = useLang();
  const navigate = useNavigate();

  return (
    <Shell>
      <motion.div
        data-testid="payment-cancel-page"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-lg text-center"
      >
        <XCircle size={64} className="mx-auto text-white/50" strokeWidth={1.4} />
        <h1 className="mt-8 font-serif text-5xl tracking-tight text-white md:text-6xl">
          {t.payment.cancelTitle}
        </h1>
        <p className="mt-5 text-base leading-relaxed text-white/65 md:text-lg">
          {t.payment.cancelText}
        </p>
        <button
          data-testid="back-home-button"
          onClick={() => navigate("/")}
          className="mt-10 rounded-full bg-primary px-9 py-4 text-[13px] font-semibold uppercase tracking-[0.14em] text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#e0562a]"
        >
          {t.payment.backHome}
        </button>
      </motion.div>
    </Shell>
  );
}
